import { useCallback, useEffect, useRef, useState } from 'react'

import { useAppAuth } from '@/lib/auth/client'

const DEFAULT_CUSTOMER_QUERY_TTL_MS = 5 * 60 * 1000
const MAX_CUSTOMER_QUERY_CACHE_ENTRIES = 40
/**
 * An answer this young is "just read": a mount that revalidates takes it as it is, and a reader of
 * the same `requestKey` in another scope shares it instead of asking again.
 */
const JUST_READ_MS = 2_000

interface CustomerQueryCacheEntry<T> {
  data: T
  updatedAt: number
}

interface CachedCustomerQuery<T> extends CustomerQueryCacheEntry<T> {
  isFresh: boolean
}

const customerQueryCache = new Map<string, CustomerQueryCacheEntry<unknown>>()
const inFlightCustomerQueries = new Map<string, Promise<unknown>>()
const inFlightCustomerQueryTokens = new Map<string, symbol>()

/**
 * One network read per `requestKey`, across cache scopes.
 *
 * A page scopes its cache by the store's company, which a hard load learns only from the
 * bootstrap; the prefetch that overlaps the bootstrap has to start before it. Both name the same
 * request (`requestKey`, the path the API Worker answers for the caller whatever the scope), so
 * whoever asks while it is in flight, or just after it answered, shares it. Keyed by user and
 * logical cache key too, so it never crosses users and `invalidateCustomerQueryCache` retires it.
 */
interface SharedCustomerRequest {
  promise: Promise<unknown>
  settledAt: number | null
}
const sharedCustomerRequests = new Map<string, SharedCustomerRequest>()

function sharedCustomerRequestKey(userScopeKey: string | null, requestKey: string, cacheKey: string) {
  return `${userScopeKey ?? 'anonymous'}:${requestKey}:${cacheKey}`
}

function isJustRead(settledAt: number) {
  return Date.now() - settledAt <= JUST_READ_MS
}

function runSharedCustomerRequest<T>(sharedKey: string, queryFn: () => Promise<T>, forceRefresh: boolean): Promise<T> {
  const existing = sharedCustomerRequests.get(sharedKey)
  if (existing && !forceRefresh && (existing.settledAt === null || isJustRead(existing.settledAt))) {
    return existing.promise as Promise<T>
  }

  for (const [key, entry] of sharedCustomerRequests) {
    if (entry.settledAt !== null && !isJustRead(entry.settledAt)) sharedCustomerRequests.delete(key)
  }
  const promise = queryFn()
  const entry: SharedCustomerRequest = { promise, settledAt: null }
  sharedCustomerRequests.set(sharedKey, entry)
  promise.then(
    () => {
      entry.settledAt = Date.now()
    },
    () => {
      // A failure is never shared: the next reader asks again.
      if (sharedCustomerRequests.get(sharedKey) === entry) sharedCustomerRequests.delete(sharedKey)
    },
  )
  return promise
}

function scopedCustomerQueryCacheKey(
  userScopeKey: string | null,
  scopeKey: string | null | undefined,
  cacheKey: string,
) {
  return `${userScopeKey ?? 'anonymous'}:${scopeKey ?? 'default'}:${cacheKey}`
}

function readCustomerQueryCache<T>(cacheKey: string, ttlMs: number): CachedCustomerQuery<T> | undefined {
  const entry = customerQueryCache.get(cacheKey) as CustomerQueryCacheEntry<T> | undefined
  if (!entry) {
    return undefined
  }

  customerQueryCache.delete(cacheKey)
  customerQueryCache.set(cacheKey, entry)
  return {
    ...entry,
    isFresh: Date.now() - entry.updatedAt <= ttlMs,
  }
}

function writeCustomerQueryCache<T>(cacheKey: string, data: T) {
  customerQueryCache.delete(cacheKey)
  customerQueryCache.set(cacheKey, { data, updatedAt: Date.now() })

  while (customerQueryCache.size > MAX_CUSTOMER_QUERY_CACHE_ENTRIES) {
    const oldestKey = customerQueryCache.keys().next().value as string | undefined
    if (!oldestKey) break
    customerQueryCache.delete(oldestKey)
  }
}

function invalidateScopedCustomerQueryCache(cacheKey: string) {
  customerQueryCache.delete(cacheKey)
  inFlightCustomerQueries.delete(cacheKey)
  inFlightCustomerQueryTokens.delete(cacheKey)
  notifyIfSettled()
}

const settleWaiters = new Set<() => void>()

function notifyIfSettled() {
  if (inFlightCustomerQueries.size > 0) return
  for (const resolve of [...settleWaiters]) resolve()
  settleWaiters.clear()
}

/**
 * Resolves once no customer query is in flight (at once when none is), or after `maxWaitMs`
 * whatever is still pending. For work that should not compete with a page's own reads on a cold
 * load: the shell's analytics SDK and its sibling-route prefetch wait for this.
 */
export function whenCustomerQueriesSettle(maxWaitMs = 10_000): Promise<void> {
  if (inFlightCustomerQueries.size === 0) return Promise.resolve()
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer)
      settleWaiters.delete(done)
      resolve()
    }
    const timer = setTimeout(done, maxWaitMs)
    settleWaiters.add(done)
  })
}

export function invalidateCustomerQueryCache(cacheKey: string) {
  const logicalCacheKeySuffix = `:${cacheKey}`
  const scopedCacheKeys = new Set([
    ...customerQueryCache.keys(),
    ...inFlightCustomerQueries.keys(),
    ...inFlightCustomerQueryTokens.keys(),
  ])

  for (const scopedCacheKey of scopedCacheKeys) {
    if (scopedCacheKey.endsWith(logicalCacheKeySuffix)) {
      invalidateScopedCustomerQueryCache(scopedCacheKey)
    }
  }
  for (const sharedKey of [...sharedCustomerRequests.keys()]) {
    if (sharedKey.endsWith(logicalCacheKeySuffix)) sharedCustomerRequests.delete(sharedKey)
  }
}

/**
 * The refetchers of the hooks mounted right now, by logical cache key. `invalidateCustomerQueryCache`
 * only clears the cache, so a screen already showing the old answer keeps it until something reads
 * again; `refreshCustomerQuery` is the invalidate that also makes every mounted reader read again.
 */
const mountedRefetchers = new Map<string, Set<() => Promise<void>>>()

/**
 * Settles once every mounted reader has read again (a reader's failure lands in its own state).
 * Readers of the same scope share the one read: two of them mounted (a page and the sidebar) cost
 * one request, not two.
 */
export function refreshCustomerQuery(cacheKey: string): Promise<void> {
  invalidateCustomerQueryCache(cacheKey)
  return Promise.all([...(mountedRefetchers.get(cacheKey) ?? [])].map((refetch) => refetch())).then(() => undefined)
}

function runCustomerQuery<T>(
  cacheKey: string,
  queryFn: () => Promise<T>,
  cacheEnabled: boolean,
  forceRefresh = false,
  sharedKey: string | null = null,
  /** Skip the cache but share a read already in flight: one started by this same refresh. */
  joinInFlight = false,
): Promise<T> {
  if (!cacheEnabled) {
    return queryFn()
  }
  if (sharedKey) {
    const ownQueryFn = queryFn
    queryFn = () => runSharedCustomerRequest(sharedKey, ownQueryFn, forceRefresh && !joinInFlight)
  }

  const existingRequest = inFlightCustomerQueries.get(cacheKey) as Promise<T> | undefined
  if (existingRequest && (!forceRefresh || joinInFlight)) {
    return existingRequest
  }

  const requestToken = Symbol(cacheKey)
  const request = queryFn().then((data) => {
    if (inFlightCustomerQueryTokens.get(cacheKey) === requestToken) {
      writeCustomerQueryCache(cacheKey, data)
    }
    return data
  })
  const trackedRequest = request.finally(() => {
    if (inFlightCustomerQueryTokens.get(cacheKey) === requestToken) {
      inFlightCustomerQueries.delete(cacheKey)
      inFlightCustomerQueryTokens.delete(cacheKey)
      notifyIfSettled()
    }
  })

  inFlightCustomerQueries.set(cacheKey, trackedRequest)
  inFlightCustomerQueryTokens.set(cacheKey, requestToken)
  return trackedRequest
}

interface PrefetchCustomerQueryOptions<T> {
  cacheKey: string
  userScopeKey: string
  queryFn: () => Promise<T>
  ttlMs?: number
  scopeKey?: string | null
  /** The request itself, when other scopes ask the same one: see `sharedCustomerRequests`. */
  requestKey?: string
}

export function prefetchCustomerQuery<T>({
  cacheKey,
  userScopeKey,
  queryFn,
  ttlMs = DEFAULT_CUSTOMER_QUERY_TTL_MS,
  scopeKey,
  requestKey,
}: PrefetchCustomerQueryOptions<T>): Promise<T> {
  const scopedCacheKey = scopedCustomerQueryCacheKey(userScopeKey, scopeKey, cacheKey)
  const cached = readCustomerQueryCache<T>(scopedCacheKey, ttlMs)
  if (cached?.isFresh) {
    return Promise.resolve(cached.data)
  }

  const sharedKey = requestKey ? sharedCustomerRequestKey(userScopeKey, requestKey, cacheKey) : null
  return runCustomerQuery(scopedCacheKey, queryFn, true, false, sharedKey)
}

/**
 * Starts (or joins) the read for `requestKey` without filing it under any scope yet: for a
 * prefetch that must start before it knows the scope the page will read it from.
 */
export function startSharedCustomerRequest<T>({
  cacheKey,
  userScopeKey,
  requestKey,
  queryFn,
}: {
  cacheKey: string
  userScopeKey: string
  requestKey: string
  queryFn: () => Promise<T>
}): Promise<T> {
  return runSharedCustomerRequest(sharedCustomerRequestKey(userScopeKey, requestKey, cacheKey), queryFn, false)
}

interface UseCustomerQueryOptions<T> {
  cacheKey: string
  queryFn: () => Promise<T>
  enabled?: boolean
  revalidateOnMount?: boolean
  ttlMs?: number
  scopeKey?: string | null
  /** The request itself, when other scopes ask the same one: see `sharedCustomerRequests`. */
  requestKey?: string
  /**
   * Keeps a long-open screen current: while the tab is visible the answer is read again once it is
   * this old, and at once when the customer comes back to the tab (or window) with an answer at
   * least this old. A hidden tab reads nothing. A failed read keeps the answer on screen
   * (`isStaleError`), and mounted readers of the key share one read.
   */
  refreshWhileVisibleMs?: number
}

/**
 * A failure, carrying the scope it happened in.
 *
 * One atom rather than a loose `isError`/`error` pair so the two can never
 * disagree, and so the scope travels with the failure: it is reported only
 * against the very scoped cache key whose read produced it. That pin is what
 * lets a failure OUTLIVE the retry that is trying to clear it (below) without
 * ever leaking onto a different page, filter or user.
 */
interface CustomerQueryFailure {
  scopeKey: string
  error: Error
}

export interface UseCustomerQueryResult<T> {
  data: T | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
  /**
   * A background revalidation failed while `data` is still the older cached answer. Kept apart
   * from `isError` on purpose: the rows on screen are usable, so the screen should stay up and
   * say they may be out of date, not be replaced by an error state.
   */
  isStaleError: boolean
  staleError: Error | null
  refetch: () => Promise<void>
  invalidate: () => void
  isRefetching: boolean
}

export function useCustomerQuery<T>({
  cacheKey,
  queryFn,
  enabled = true,
  revalidateOnMount = false,
  ttlMs = DEFAULT_CUSTOMER_QUERY_TTL_MS,
  scopeKey,
  requestKey,
  refreshWhileVisibleMs,
}: UseCustomerQueryOptions<T>): UseCustomerQueryResult<T> {
  const { userId: authUserId, isLoaded: isAuthLoaded } = useAppAuth()
  const userScopeKey = isAuthLoaded && typeof authUserId === 'string' ? authUserId : null
  const scopedCacheKey = scopedCustomerQueryCacheKey(userScopeKey, scopeKey, cacheKey)
  const cacheEnabled = Boolean(userScopeKey)
  const queryEnabled = enabled && cacheEnabled
  const sharedKey = requestKey ? sharedCustomerRequestKey(userScopeKey, requestKey, cacheKey) : null
  const initialCache = cacheEnabled ? readCustomerQueryCache<T>(scopedCacheKey, ttlMs) : undefined
  const [data, setData] = useState<T | undefined>(initialCache?.data)
  const [dataScopeKey, setDataScopeKey] = useState(scopedCacheKey)
  const [isLoading, setIsLoading] = useState(queryEnabled && !initialCache)
  const [failure, setFailure] = useState<CustomerQueryFailure | null>(null)
  const [staleFailure, setStaleFailure] = useState<CustomerQueryFailure | null>(null)
  const [isRefetching, setIsRefetching] = useState(false)
  const executionIdRef = useRef(0)

  /**
   * `queryFn` is read through a ref, never closed over.
   *
   * Almost every consumer passes a fresh inline arrow each render, so keeping
   * `queryFn` in `execute`'s dependency list re-identified `execute` on every
   * render, which re-ran the mount effect below, which bumped
   * `executionIdRef` — and that superseded the forced refetch that was in
   * flight, so its own rejection could never set `isError`. The first failed
   * refresh was silent. The effect now keys only on the values that genuinely
   * change WHICH data is wanted (the scoped cache key, the auth scope, the TTL
   * and the revalidate flag); a new arrow closing over the same scope is not
   * one of them.
   */
  const queryFnRef = useRef(queryFn)
  useEffect(() => {
    queryFnRef.current = queryFn
  })

  /**
   * A failure outlives its own retry. It must not outlive the QUESTION.
   *
   * Retaining a failure across a retry is the whole point of this hook's error
   * lifecycle, but the scoped cache key is what says WHICH data is being asked
   * for — so when it moves, the stored failure describes something nobody is
   * looking at any more and is dropped here.
   *
   * Deliberately an effect keyed on the scope rather than a line inside
   * `execute`. `execute` cannot be trusted to reach a clearing line: its
   * fresh-cache branch returns early before the `try` (and before `finally`),
   * and it does not run at all while `queryEnabled` is false. Any clear placed
   * inside it is therefore skippable, and a skipped clear is exactly how a
   * scope round trip — page 1 fails, operator visits page 2, operator returns —
   * used to land a live-looking failure banner on a screen with a fresh cache,
   * nothing in flight and no read to come. The updater compares before writing,
   * so a failure belonging to the CURRENT scope is returned untouched and React
   * bails out without a re-render.
   */
  useEffect(() => {
    setFailure((current) => (current && current.scopeKey === scopedCacheKey ? current : null))
    setStaleFailure((current) => (current && current.scopeKey === scopedCacheKey ? current : null))
  }, [scopedCacheKey])

  const execute = useCallback(
    async (
      forceRefresh: boolean,
      shouldUpdate: () => boolean = () => true,
      { joinInFlight = false, stale = false }: { joinInFlight?: boolean; stale?: boolean } = {},
    ) => {
      const executionId = ++executionIdRef.current
      const canUpdate = () => shouldUpdate() && executionIdRef.current === executionId
      const cached = cacheEnabled ? readCustomerQueryCache<T>(scopedCacheKey, ttlMs) : undefined
      // Revalidating on mount is for an answer from an earlier visit; one read a moment ago (a
      // prefetch that landed just before the page mounted) is this visit's.
      const revalidate = revalidateOnMount && !(cached && isJustRead(cached.updatedAt))
      if (!forceRefresh && cached?.isFresh && !revalidate && !stale) {
        if (canUpdate()) {
          setDataScopeKey(scopedCacheKey)
          setData(cached.data)
          setIsLoading(false)
          // This generation superseded whatever was in flight, and that one's
          // `finally` is now a no-op. Nothing else will ever write `false`.
          setIsRefetching(false)
        }
        return
      }

      if (canUpdate()) {
        setDataScopeKey(scopedCacheKey)
        if (cached) {
          setData(cached.data)
          setIsLoading(false)
        } else {
          setData(undefined)
          setIsLoading(true)
        }
        setIsRefetching(Boolean(cached) || forceRefresh)
        // The failure is deliberately NOT cleared here. Clearing it at the top
        // of a read meant the honesty banner vanished the moment a retry
        // STARTED and only came back if the retry also failed — so on a slow
        // read an operator saw stale rows with no warning attached, which is
        // the exact lie goals 195/196 exist to prevent. A failure is retired by
        // a SUCCESS, never by an attempt. `isRefetching`, set just above, is
        // what tells a screen "still failing, retrying…" apart from "fine";
        // the scope pin on the failure itself is what keeps it off any other
        // scope while it waits.
      }

      try {
        const result = await runCustomerQuery(
          scopedCacheKey,
          () => queryFnRef.current(),
          cacheEnabled,
          forceRefresh,
          sharedKey,
          joinInFlight,
        )
        if (canUpdate()) {
          setDataScopeKey(scopedCacheKey)
          setData(result)
          setFailure(null)
          setStaleFailure(null)
        }
      } catch (err) {
        if (canUpdate()) {
          const failed = {
            scopeKey: scopedCacheKey,
            error: err instanceof Error ? err : new Error('Unknown error'),
          }
          // A quiet revalidation behind cached rows is not an error state, but it is not nothing
          // either: the rows stay up and `isStaleError` says they could not be refreshed.
          if (!cached || forceRefresh) setFailure(failed)
          else setStaleFailure(failed)
        }
      } finally {
        if (canUpdate()) {
          setIsRefetching(false)
          setIsLoading(false)
        }
      }
    },
    [cacheEnabled, revalidateOnMount, scopedCacheKey, sharedKey, ttlMs],
  )

  useEffect(() => {
    if (!queryEnabled) {
      // A `refetch()` generation is retired only by a LATER `execute`, and a
      // disabled query issues none — so without this bump an abandoned
      // refetch stays current and its rejection lands on the scope it was
      // closed over, for the scope-reset effect to keep faithfully.
      executionIdRef.current += 1
      setIsLoading(false)
      setIsRefetching(false)
      return
    }

    let active = true
    void execute(false, () => active)
    return () => {
      active = false
    }
  }, [execute, queryEnabled])

  const executeRef = useRef(execute)
  useEffect(() => {
    executeRef.current = execute
  })
  useEffect(() => {
    if (!queryEnabled) return
    const refetchNow = () => executeRef.current(true, undefined, { joinInFlight: true })
    const set = mountedRefetchers.get(cacheKey) ?? new Set<() => Promise<void>>()
    set.add(refetchNow)
    mountedRefetchers.set(cacheKey, set)
    return () => {
      set.delete(refetchNow)
      if (set.size === 0 && mountedRefetchers.get(cacheKey) === set) mountedRefetchers.delete(cacheKey)
    }
  }, [cacheKey, queryEnabled])

  useEffect(() => {
    if (!queryEnabled || !refreshWhileVisibleMs) return
    // A read that failed leaves no cache entry, so age is measured from the last attempt too:
    // a 404 (no company yet) is asked again once per period, not on every check.
    let lastAttempt = Date.now()
    const refreshIfOld = () => {
      if (document.visibilityState !== 'visible') return
      const entry = customerQueryCache.get(scopedCacheKey)
      const newest = Math.max(entry?.updatedAt ?? 0, lastAttempt)
      if (Date.now() - newest < refreshWhileVisibleMs) return
      lastAttempt = Date.now()
      void executeRef.current(false, undefined, { stale: true })
    }
    // Checked four times per period, so an answer read mid-period is read again at most a quarter
    // period late.
    const timer = setInterval(refreshIfOld, refreshWhileVisibleMs / 4)
    document.addEventListener('visibilitychange', refreshIfOld)
    window.addEventListener('focus', refreshIfOld)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', refreshIfOld)
      window.removeEventListener('focus', refreshIfOld)
    }
  }, [queryEnabled, refreshWhileVisibleMs, scopedCacheKey])

  const isCurrentScope = dataScopeKey === scopedCacheKey
  // Gated on the failure's OWN scope, not on `dataScopeKey`: `execute` moves
  // `dataScopeKey` to the new key before the new read has answered anything, so
  // reusing that gate would have surfaced the old scope's failure against the
  // new one the instant a scope changed.
  const activeFailure = failure && failure.scopeKey === scopedCacheKey ? failure : null
  const activeStaleFailure = staleFailure && staleFailure.scopeKey === scopedCacheKey ? staleFailure : null
  const invalidate = useCallback(() => invalidateScopedCustomerQueryCache(scopedCacheKey), [scopedCacheKey])

  return {
    data: isCurrentScope ? data : initialCache?.data,
    isLoading: isCurrentScope ? isLoading : queryEnabled && !initialCache,
    isError: activeFailure !== null,
    error: activeFailure?.error ?? null,
    isStaleError: activeStaleFailure !== null && activeFailure === null,
    staleError: activeFailure === null ? (activeStaleFailure?.error ?? null) : null,
    refetch: () => execute(true),
    invalidate,
    isRefetching: isCurrentScope ? isRefetching : Boolean(initialCache),
  }
}
